import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { FlashKind } from '@shared/types/flash';

import { Spinner } from '@portal/components/helpers';
import { orderURL } from '@portal/config/routes';
import {
  OrderCoiRequestInput,
  OrderDetailsDocument,
  Status,
  useBuildOrderCoiRequestMutation,
  useOrderCertificateOfInsuranceQuery,
} from '@portal/schema';

import { OrderCOIForm } from './certificate_of_insurance/form';
import { Container } from './container';

const MESSAGES = {
  SUCCESS: 'The request for a COI has been successfully sent!',
  ERROR: 'The request could not be submitted. Contact Customer Care for assistance.',
} as const;

export const CertificateOfInsurance: React.FC<RouteComponentProps<{ orderID: string }>> = ({
  match: {
    params: { orderID },
  },
  history,
}) => {
  const [error, setError] = useState<string>();
  const { data, loading: queryLoading } = useOrderCertificateOfInsuranceQuery({ variables: { orderID } });
  const [execute, { loading: updating }] = useBuildOrderCoiRequestMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: OrderDetailsDocument, variables: { orderID } }],
  });

  const handleSubmit = async (input: OrderCoiRequestInput) => {
    setError(undefined);

    const response = await execute({
      variables: { input: { ...input, orderID } },
    });

    const result = response.data?.buildOrderCoiRequest;

    if (result?.status === Status.Ok) {
      history.push(orderURL(orderID), {
        flash: {
          kind: FlashKind.Success,
          message: MESSAGES.SUCCESS,
        },
      });
    } else {
      setError(result?.error || MESSAGES.ERROR);
    }
  };

  if (queryLoading || !data) {
    return <Spinner />;
  }

  const { order, addresses } = data;

  return (
    <Container breadcrumb="COI / Request a COI" orderID={order.id} scheduled={order.scheduled} error={error}>
      <OrderCOIForm
        email={order.account.customer.email}
        loading={updating}
        onSubmit={handleSubmit}
        addresses={addresses}
      />
    </Container>
  );
};
